import { useEffect, useMemo, useState } from "react";
import { createMarketplaceConnectOnboarding, getMarketplaceConnectStatus, getMarketplaceLedger } from "../../api/myboothshareBilling";
import RentalsShell from "../manager/rentals/RentalsShell";
import { useAuth } from "../../context/AuthContext";
import { formatMarketplaceMoneyFromCents } from "../../utils/myboothshareBillingPresentation";

function MetricCard({ label, value, detail }) {
  return (
    <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-3 text-3xl font-semibold text-slate-950">{value}</div>
      <div className="mt-2 text-sm text-slate-600">{detail}</div>
    </article>
  );
}

export default function OperatorPayoutsPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([getMarketplaceConnectStatus(token), getMarketplaceLedger(token)])
      .then(([account, ledgerResponse]) => {
        if (!active) return;
        setStatus(account);
        setLedger(Array.isArray(ledgerResponse?.items) ? ledgerResponse.items : []);
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail ||
              err.message ||
              "Unable to load payout onboarding."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const metrics = useMemo(() => {
    const pending = ledger.filter(
      (item) => !item.dispute_open && String(item.transfer_status || "").toLowerCase() !== "released"
    );
    const released = ledger.filter(
      (item) => String(item.transfer_status || "").toLowerCase() === "released"
    );
    const disputed = ledger.filter((item) => item.dispute_open);
    return {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, item) => sum + Number(item.net_payout_cents || 0), 0),
      releasedAmount: released.reduce((sum, item) => sum + Number(item.net_payout_cents || 0), 0),
      disputedCount: disputed.length,
    };
  }, [ledger]);

  const handleOnboard = async () => {
    setLinkLoading(true);
    setError("");
    try {
      const response = await createMarketplaceConnectOnboarding(token, {});
      if (response?.onboarding_url) {
        window.location.assign(response.onboarding_url);
        return;
      }
      setError("Stripe onboarding did not return a redirect URL.");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Unable to start Stripe Connect onboarding."
      );
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <RentalsShell
      eyebrow="Ellyanna Boothshare Billing"
      title="Payout onboarding"
      description="Connect the salon owner payout account, review payout readiness, and confirm which Stripe requirements still need attention."
      actions={
        <button
          type="button"
          onClick={handleOnboard}
          disabled={linkLoading}
          className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {linkLoading ? "Opening Stripe..." : "Connect Stripe account"}
        </button>
      }
    >
      <section className="space-y-6">
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Charges"
            value={loading ? "..." : status?.charges_enabled ? "Enabled" : "Not ready"}
            detail="Renter payments can only be released after the connected account is fully enabled."
          />
          <MetricCard
            label="Payouts"
            value={loading ? "..." : status?.payouts_enabled ? "Enabled" : "Not ready"}
            detail="The connected account must be payout-ready before funds can be transferred."
          />
          <MetricCard
            label="Pending release"
            value={loading ? "..." : formatMarketplaceMoneyFromCents(metrics.pendingAmount, "cad")}
            detail={`${metrics.pendingCount} booking payouts are still waiting for release conditions.`}
          />
          <MetricCard
            label="Released"
            value={loading ? "..." : formatMarketplaceMoneyFromCents(metrics.releasedAmount, "cad")}
            detail={`${metrics.disputedCount} payout${metrics.disputedCount === 1 ? "" : "s"} currently paused by disputes.`}
          />
        </section>

        <section className="grid gap-4 lg:grid-cols-[0.9fr,1.1fr]">
          <article className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
              Connect status
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-slate-950">
              {loading ? "Loading..." : status?.connected ? "Connected account found" : "No connected account yet"}
            </h2>
            <dl className="mt-5 space-y-4 text-sm text-slate-600">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <dt className="font-semibold text-slate-900">Onboarding status</dt>
                <dd className="mt-1">{status?.onboarding_status || "not_started"}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <dt className="font-semibold text-slate-900">Default payout currency</dt>
                <dd className="mt-1">{(status?.default_payout_currency || "cad").toUpperCase()}</dd>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <dt className="font-semibold text-slate-900">Country</dt>
                <dd className="mt-1">{status?.country || "CA"}</dd>
              </div>
            </dl>
          </article>

          <article className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Requirements due
            </p>
            {loading ? (
              <p className="mt-4 text-sm text-slate-600">Loading Stripe requirements...</p>
            ) : Array.isArray(status?.requirements_due) && status.requirements_due.length ? (
              <div className="mt-4 space-y-3">
                {status.requirements_due.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                  >
                    {item}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
                No outstanding Stripe requirements are currently blocking payouts.
              </div>
            )}
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              Ellyanna Boothshare automatically holds renter funds until the booking ends and the review
              window passes. Once that happens, payout release runs automatically unless a dispute
              pauses the release.
            </div>
          </article>
        </section>
      </section>
    </RentalsShell>
  );
}
